import { ReactElement, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import NewButton from "../../../components/Atomos/Buttons/newButton";
import TableHorizontalScrollContainer from "../../../components/Organismos/Table/TableHorizontalScrollContainer";
import { ManageSecondmentRow } from "../../../Services/ManageSecondments";

const PAGE_SIZE = 10;

type SortKey = "title" | "deadlineSort" | "submittedSort" | "active";

interface ManageSecondmentsTableProps {
  rows: ManageSecondmentRow[];
  onDelete: (secId: number) => Promise<void>;
}

function compareRows(left: ManageSecondmentRow, right: ManageSecondmentRow, key: SortKey, direction: number): number {
  const leftValue = left[key];
  const rightValue = right[key];
  if (typeof leftValue === "number" && typeof rightValue === "number") {
    return (leftValue - rightValue) * direction;
  }
  return String(leftValue).localeCompare(String(rightValue)) * direction;
}

function csvCell(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

function downloadCsv(rows: ManageSecondmentRow[]): void {
  const header = ["#", "Position Title", "App. Deadline", "Submission Date", "Active"];
  const lines = rows.map((row, index) => [String(index + 1), row.title, row.deadline, row.submittedDate, row.active].map(csvCell).join(","));
  const blob = new Blob([[header.join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "FIRM SECONDMENT LIST - World Services Group.csv";
  link.click();
  URL.revokeObjectURL(url);
}

export default function ManageSecondmentsTable({ rows, onDelete }: ManageSecondmentsTableProps): ReactElement {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [direction, setDirection] = useState(1);
  const sorted = useMemo(() => {
    if (!sortKey) {
      return rows;
    }
    return [...rows].sort((left, right) => compareRows(left, right, sortKey, direction));
  }, [rows, sortKey, direction]);
  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const visible = sorted.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
  const from = sorted.length === 0 ? 0 : safePage * PAGE_SIZE + 1;
  const to = sorted.length === 0 ? 0 : safePage * PAGE_SIZE + visible.length;

  function toggleSort(key: SortKey): void {
    if (sortKey === key) {
      setDirection(direction * -1);
    } else {
      setSortKey(key);
      setDirection(1);
    }
    setPage(0);
  }

  function openPosting(row: ManageSecondmentRow): void {
    window.open(`/secondments?view=1&action=view&secid=${row.secId}&firm=${row.firmId}`, "Profile", "width=1050,height=800");
  }

  return (
    <section className="stack-tight">
      <h2 className="section-bar">Firm Secondment List</h2>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <NewButton text="Export to Excel" color="excel" size="small" onClick={() => downloadCsv(sorted)} />
          <p className="caption">Showing {from} to {to} of {sorted.length} entries</p>
        </div>
        <div className="flex gap-2">
          <NewButton text="First" color="neutral" size="small" disabled={safePage === 0} onClick={() => setPage(0)} />
          <NewButton text="Prev" color="neutral" size="small" disabled={safePage === 0} onClick={() => setPage(Math.max(0, safePage - 1))} />
          <NewButton text="Next" color="neutral" size="small" disabled={safePage >= pageCount - 1} onClick={() => setPage(Math.min(pageCount - 1, safePage + 1))} />
          <NewButton text="Last" color="neutral" size="small" disabled={safePage >= pageCount - 1} onClick={() => setPage(pageCount - 1)} />
        </div>
      </div>
      <div className="overflow-hidden border border-gray-200 bg-white">
        <TableHorizontalScrollContainer measureKey={visible.length}>
          <table className="manage-table min-w-full border-collapse text-sm text-gray-font">
            <thead>
              <tr>
                <th>#</th>
                <th><button type="button" onClick={() => toggleSort("title")}>Position Title</button></th>
                <th><button type="button" onClick={() => toggleSort("deadlineSort")}>App. Deadline</button></th>
                <th><button type="button" onClick={() => toggleSort("submittedSort")}>Submission Date</button></th>
                <th><button type="button" onClick={() => toggleSort("active")}>Active</button></th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr>
                  <td colSpan={6}>No data available in table</td>
                </tr>
              ) : visible.map((row, index) => (
                <tr key={row.secId}>
                  <td>{safePage * PAGE_SIZE + index + 1}</td>
                  <td><button type="button" className="text-left text-primary underline" onClick={() => openPosting(row)}>{row.title}</button></td>
                  <td>{row.deadline}</td>
                  <td>{row.submittedDate}</td>
                  <td>{row.active}</td>
                  <td>
                    <div className="flex flex-wrap gap-2">
                      <NewButton text="View" size="small" onClick={() => openPosting(row)} />
                      <NewButton text="Edit" size="small" onClick={() => navigate(`/manage_secondments?action=update&id=${row.secId}`)} />
                      <NewButton text="Delete" size="small" onClick={() => { void onDelete(row.secId); }} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableHorizontalScrollContainer>
      </div>
    </section>
  );
}
